"use client";

import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Compass,
  ArrowUp,
  X,
  ChevronRight,
  Clock,
  Check,
  Copy,
} from "lucide-react";
import { getCategoryTheme } from "@/lib/tile-categories";
import { slugifyText } from "@/lib/slugify";

export interface HeadingItem {
  id: string;
  title: string;
  level: 1 | 2 | 3;
}

interface Props {
  category?: string;
  title?: string;
  isWide?: boolean;
}

export default function TableOfContents({
  category,
  title,
  isWide = false,
}: Props) {
  const [headings, setHeadings] = useState<HeadingItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [progress, setProgress] = useState<number>(0);
  const [readingTime, setReadingTime] = useState<number>(1);
  const [isOpenMobile, setIsOpenMobile] = useState<boolean>(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const theme = useMemo(() => getCategoryTheme(category), [category]);

  // Extract headings and calculate reading time from article DOM
  const scanHeadings = useCallback(() => {
    const article = document.querySelector("article");
    if (!article) return;

    // Word count / reading time
    const text = article.innerText || "";
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    setReadingTime(Math.max(1, Math.ceil(words / 200)));

    const items: HeadingItem[] = [];

    // 1. H1 (Page Title / Document Top)
    const h1El = document.querySelector<HTMLElement>("h1");
    const h1Title = title || h1El?.textContent?.trim() || "";
    const h1Id = h1El?.id || "article-title";
    if (h1El && !h1El.id) {
      h1El.id = h1Id;
    }

    if (h1Title) {
      items.push({
        id: h1Id,
        title: h1Title,
        level: 1,
      });
    }

    // 1b. Optional Tool Stage right below page header (layout === "tool")
    const toolStageEl = document.getElementById("tool-hero-stage");
    if (toolStageEl) {
      items.push({
        id: "tool-hero-stage",
        title: "Interaktives Werkzeug",
        level: 2,
      });
    }

    // 2. Find all H2 and H3 elements within the article
    const headingElements = Array.from(
      article.querySelectorAll<HTMLElement>("h2, h3")
    );

    headingElements.forEach((el) => {
      // Determine heading level
      const level = el.tagName.toUpperCase() === "H2" ? 2 : 3;

      // Ensure el has an id
      let id = el.id;
      if (!id) {
        id = slugifyText(el.innerText || "").trim();
        if (id) {
          el.id = id;
        }
      }

      // Extract clean text (strip out copy buttons or hidden characters)
      const clone = el.cloneNode(true) as HTMLElement;
      clone.querySelectorAll("button, a, svg, .sr-only").forEach((n) => n.remove());
      const cleanTitle = (clone.textContent || el.innerText || "")
        .replace(/#+$/, "")
        .trim();

      if (id && cleanTitle && cleanTitle !== "#") {
        items.push({ id, title: cleanTitle, level });
      }
    });

    setHeadings(items);
    if (items.length > 0 && !activeId) {
      setActiveId(items[0].id);
    }
  }, [activeId, title]);

  useEffect(() => {
    // Run after DOM has painted
    const timer = setTimeout(scanHeadings, 150);

    // Also observe DOM mutations inside article (e.g. client components hydrating)
    const article = document.querySelector("article");
    let mutationObserver: MutationObserver | null = null;
    if (article) {
      mutationObserver = new MutationObserver(() => {
        scanHeadings();
      });
      mutationObserver.observe(article, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      clearTimeout(timer);
      if (mutationObserver) mutationObserver.disconnect();
    };
  }, [scanHeadings]);

  // Track active heading and reading progress on scroll
  useEffect(() => {
    if (headings.length === 0) return;

    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        ticking = false;

        const scrollY = window.scrollY;
        const windowHeight = window.innerHeight;
        const docHeight = document.documentElement.scrollHeight;
        const totalScrollable = docHeight - windowHeight;

        // Reading progress percentage (0 - 100)
        const pct =
          totalScrollable > 0
            ? Math.min(100, Math.max(0, Math.round((scrollY / totalScrollable) * 100)))
            : 0;
        setProgress(pct);

        // Top edge: first item active
        if (scrollY < 140) {
          setActiveId(headings[0].id);
          return;
        }

        // Bottom edge: last item active
        if (windowHeight + scrollY >= docHeight - 80) {
          setActiveId(headings[headings.length - 1].id);
          return;
        }

        // Find the heading that is closest above or at the reading offset (120px)
        const offset = 130;
        let currentActive = headings[0].id;

        for (let i = 0; i < headings.length; i++) {
          const el = document.getElementById(headings[i].id);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= offset) {
              currentActive = headings[i].id;
            } else {
              break;
            }
          }
        }

        setActiveId(currentActive);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [headings]);

  // Close mobile drawer on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpenMobile) {
        setIsOpenMobile(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpenMobile]);

  // Prevent background scroll when mobile drawer is open
  useEffect(() => {
    if (isOpenMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpenMobile]);

  const scrollToHeading = useCallback((id: string) => {
    if (id === "article-title" || id === "overview") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setIsOpenMobile(false);
      return;
    }
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth" });
    setIsOpenMobile(false);
  }, []);

  const handleCopyLink = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const url =
        id === "article-title" || id === "overview"
          ? `${window.location.origin}${window.location.pathname}`
          : `${window.location.origin}${window.location.pathname}#${id}`;
      navigator.clipboard?.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    },
    []
  );

  // If there are fewer than 2 headings, outline is unnecessary
  if (headings.length < 2) {
    return null;
  }

  const activeHeading = headings.find((h) => h.id === activeId);

  return (
    <>
      {/* ───────────────────────────────────────────────────────────── */}
      {/* DESKTOP SIDEBAR (Freistehend am linken Bildschirmrand)        */}
      {/* ───────────────────────────────────────────────────────────── */}
      {!isWide && (
        <aside
          aria-label="Inhaltsverzeichnis"
          className="hidden min-[1320px]:block fixed left-6 2xl:left-10 top-32 w-48 2xl:w-52 max-h-[calc(100vh-10rem)] overflow-y-auto pr-2 pb-6 select-none z-30"
        >
        <div className="py-1">
          {/* Header: Nur "Inhalt" in Weiß und Lesedauer */}
          <div className="flex items-center justify-between pb-3">
            <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-900 dark:text-white">
              Inhalt
            </span>
            <span className="text-[10.5px] font-normal text-neutral-400 dark:text-neutral-500">
              ~{readingTime} Min.
            </span>
          </div>

          {/* Heading Tree Navigation mit zarten Hierarchie-Linien */}
          <nav aria-label="Seitenabschnitte">
            <ul className="relative space-y-1 text-xs border-l border-neutral-200/80 dark:border-neutral-800/80 pl-2.5">
              {headings.map((item) => {
                const isActive = item.id === activeId;
                const isH1 = item.level === 1;
                const isH3 = item.level === 3;

                return (
                  <li key={item.id} className="relative group cursor-pointer">
                    {/* Active indicator bar on the left hairline for H1 and H2 */}
                    {isActive && !isH3 && (
                      <span
                        className={`absolute -left-[11px] top-1.5 h-3.5 w-[2px] rounded-full ${theme.dotClass} transition-all`}
                        aria-hidden="true"
                      />
                    )}

                    <div className="flex items-center justify-between group/link">
                      <button
                        type="button"
                        onClick={() => scrollToHeading(item.id)}
                        className={`flex-1 text-left py-0.5 transition-colors duration-150 rounded select-none flex items-center gap-1.5 cursor-pointer focus-visible:outline-hidden ${
                          isH3
                            ? "pl-2 text-[11px] relative before:absolute before:-left-2.5 before:top-2 before:w-1.5 before:h-px before:bg-neutral-200 dark:before:bg-neutral-800"
                            : "text-[12px]"
                        } ${
                          isActive
                            ? "font-semibold text-neutral-900 dark:text-white"
                            : "text-neutral-400 hover:text-neutral-700 dark:text-neutral-500 dark:hover:text-neutral-300"
                        }`}
                        aria-current={isActive ? "location" : undefined}
                      >
                        {isActive && isH3 && (
                          <span
                            className={`h-1.5 w-1.5 shrink-0 rounded-full ${theme.dotClass}`}
                            aria-hidden="true"
                          />
                        )}
                        <span className="line-clamp-2 leading-relaxed cursor-pointer">
                          {item.title}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => handleCopyLink(item.id, e)}
                        className="opacity-0 group-hover/link:opacity-70 hover:opacity-100 p-0.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-opacity rounded cursor-pointer focus-visible:opacity-100"
                        title={isH1 ? "Seiten-Link kopieren" : "Abschnitt-Link kopieren"}
                        aria-label={isH1 ? "Seiten-Link kopieren" : `Link zu ${item.title} kopieren`}
                      >
                        {copiedId === item.id ? (
                          <Check className="h-3 w-3 text-emerald-500" />
                        ) : (
                          <Copy className="h-2.5 w-2.5" />
                        )}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
        </aside>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* MOBILE / TABLET FLOATING QUICK-OVERVIEW BUTTON                */}
      {/* Fixed bottom-right on screens < 1320px (or always on wide)    */}
      {/* ───────────────────────────────────────────────────────────── */}
      <div
        className={`fixed bottom-5 right-5 z-40 transition-transform duration-200 ${
          isWide ? "" : "min-[1320px]:hidden"
        }`}
      >
        <button
          type="button"
          onClick={() => setIsOpenMobile(true)}
          className="group flex items-center gap-2.5 rounded-full border border-neutral-200/90 bg-white/90 px-3.5 py-2.5 text-neutral-900 shadow-lg shadow-neutral-900/10 backdrop-blur-xl transition-all duration-200 hover:scale-[1.03] active:scale-[0.98] dark:border-neutral-800/90 dark:bg-neutral-900/90 dark:text-white dark:shadow-black/40 min-h-[48px] min-w-[48px] cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500"
          aria-label="Schnellübersicht öffnen"
          aria-expanded={isOpenMobile}
        >
          {/* Mini Circular Progress Ring */}
          <div className="relative flex h-6 w-6 shrink-0 items-center justify-center">
            <svg className="h-6 w-6 -rotate-90" viewBox="0 0 36 36">
              <path
                className="text-neutral-200 dark:text-neutral-800"
                strokeWidth="3.5"
                stroke="currentColor"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                strokeWidth="3.5"
                strokeDasharray={`${progress}, 100`}
                strokeLinecap="round"
                stroke="currentColor"
                fill="none"
                style={{ color: theme.progressBarColor }}
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <Compass className="absolute h-3 w-3 text-neutral-700 dark:text-neutral-300" />
          </div>

          {/* Active section preview or label */}
          <div className="flex flex-col text-left pr-1 max-w-[150px] sm:max-w-[200px]">
            <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
              Schnellübersicht
            </span>
            <span className="truncate text-xs font-semibold text-neutral-800 dark:text-neutral-200">
              {activeHeading ? activeHeading.title : "Inhalt"}
            </span>
          </div>

          <ChevronRight className="h-4 w-4 text-neutral-400 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* ───────────────────────────────────────────────────────────── */}
      {/* MOBILE BOTTOM SHEET MODAL (DRAWER)                            */}
      {/* ───────────────────────────────────────────────────────────── */}
      {isOpenMobile && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Schnellübersicht und Inhaltsverzeichnis"
          className="fixed inset-0 z-50 flex flex-col justify-end"
        >
          {/* Backdrop blur */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
            onClick={() => setIsOpenMobile(false)}
            aria-hidden="true"
          />

          {/* Bottom Sheet Card */}
          <div className="relative z-10 flex max-h-[82vh] w-full flex-col rounded-t-[28px] border-t border-neutral-200/90 bg-white dark:border-neutral-800 dark:bg-neutral-950 shadow-2xl p-5 pb-8 animate-in slide-in-from-bottom duration-250">
            {/* Top Drag Handle Pill */}
            <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-neutral-300 dark:bg-neutral-700" />

            {/* Header: Title, Category & Close Button */}
            <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800/80 pb-3 mb-3">
              <div className="flex items-center gap-2">
                <Compass className="h-4 w-4 text-neutral-500" />
                <h3 className="font-heading text-lg font-bold tracking-tight text-neutral-900 dark:text-white">
                  Schnellübersicht
                </h3>
                {category && (
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${theme.badgeClass}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${theme.dotClass}`} />
                    {category}
                  </span>
                )}
              </div>

              <button
                type="button"
                onClick={() => setIsOpenMobile(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-white transition-colors cursor-pointer"
                aria-label="Schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Reading time in drawer */}
            <div className="mb-3 flex items-center justify-between px-1 text-xs text-neutral-500 dark:text-neutral-400">
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                <span>~{readingTime} Min. Lesezeit</span>
              </div>
            </div>

            {/* Scrollable list of headings */}
            <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
              {headings.map((item, index) => {
                const isActive = item.id === activeId;
                const isH1 = item.level === 1;
                const isH3 = item.level === 3;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => scrollToHeading(item.id)}
                    className={`w-full flex items-center justify-between gap-3 px-3.5 py-3 rounded-xl text-left transition-all min-h-[48px] cursor-pointer ${
                      isH3 ? "pl-7 text-xs" : isH1 ? "font-semibold text-sm" : "font-medium text-sm"
                    } ${
                      isActive
                        ? `${theme.activeBgClass} ${theme.activeTextClass} ring-1 ring-inset ${theme.activeBorderClass}`
                        : "text-neutral-700 hover:bg-neutral-100/70 dark:text-neutral-300 dark:hover:bg-neutral-900/70"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      {isH1 ? (
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-[10px] font-bold text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
                          <ArrowUp className="h-3 w-3" />
                        </span>
                      ) : !isH3 ? (
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-[10px] font-bold text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
                          {index}
                        </span>
                      ) : (
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-300 dark:bg-neutral-600" />
                      )}
                      <span className="truncate leading-snug">{item.title}</span>
                    </div>

                    {isActive && (
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${theme.dotClass}`}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Bottom Actions */}
            <div className="mt-4 pt-3 border-t border-neutral-100 dark:border-neutral-800">
              <button
                type="button"
                onClick={() => setIsOpenMobile(false)}
                className="w-full inline-flex items-center justify-center rounded-xl bg-neutral-900 px-4 py-2.5 text-xs font-semibold text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-100 transition-colors min-h-[44px] cursor-pointer"
              >
                Fertig
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
